import { useState, useEffect, useRef } from 'react';
import { IconClose, IconArrowDown } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Input } from '@components/Input';
import { Button } from '@components/Button';
import { MenuSelect, type MenuSelectItem } from '@components/MenuSelect';
import type { ApolloSequence } from '@/services/firebase/firestore';
import styles from './EditMetadataModal.module.scss';

interface ObjectiveOrPainpoint {
  id: string;
  text: string;
}

export interface EditMetadataModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Close handler */
  onClose: () => void;

  /** Business type name to display in the title */
  typeName: string;

  /** Initial website objectives */
  websiteObjectives: string[];

  /** Initial industry painpoints */
  industryPainpoints: string[];

  /** Save handler - called when user clicks Save Changes */
  onSave?: (data: {
    websiteObjectives: string[];
    industryPainpoints: string[];
    apolloSequence: string | null;
  }) => void;

  /** Default Apollo Sequence ID (optional) */
  defaultApolloSequence?: string;

  /** Available Apollo sequences for the dropdown */
  availableSequences?: ApolloSequence[];
}

export const EditMetadataModal = ({
  isOpen,
  onClose,
  typeName,
  websiteObjectives: initialObjectives,
  industryPainpoints: initialPainpoints,
  onSave,
  defaultApolloSequence: initialApolloSequence,
  availableSequences = [],
}: EditMetadataModalProps) => {
  const [objectives, setObjectives] = useState<ObjectiveOrPainpoint[]>([]);
  const [painpoints, setPainpoints] = useState<ObjectiveOrPainpoint[]>([]);
  const [apolloSequence, setApolloSequence] = useState<string | null>(null);
  const [isSequenceDropdownOpen, setIsSequenceDropdownOpen] = useState(false);
  const sequenceDropdownRef = useRef<HTMLDivElement>(null);
  const prevIsOpenRef = useRef(false);

  // Initialize state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setObjectives(
        initialObjectives.map((text, index) => ({
          id: `obj-${index + 1}`,
          text,
        }))
      );
      setPainpoints(
        initialPainpoints.map((text, index) => ({
          id: `pp-${index + 1}`,
          text,
        }))
      );
      setApolloSequence(initialApolloSequence ?? null);
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only capture props when modal opens
  }, [isOpen]);

  // Close sequence dropdown on outside click
  useEffect(() => {
    if (!isSequenceDropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        sequenceDropdownRef.current &&
        !sequenceDropdownRef.current.contains(e.target as Node)
      ) {
        setIsSequenceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSequenceDropdownOpen]);

  if (!isOpen) return null;

  const sequenceItems: MenuSelectItem[] = [
    { value: '__none__', label: 'None (Default)' },
    ...availableSequences.map((seq) => ({
      value: seq.sequence_id,
      label: seq.name,
    })),
  ];

  const selectedSequenceName = apolloSequence
    ? (availableSequences.find((s) => s.sequence_id === apolloSequence)?.name ?? apolloSequence)
    : null;

  const handleSequenceSelect = (val: string) => {
    setApolloSequence(val === '__none__' ? null : val);
    setIsSequenceDropdownOpen(false);
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        websiteObjectives: objectives.map((obj) => obj.text).filter((text) => text.trim() !== ''),
        industryPainpoints: painpoints.map((pp) => pp.text).filter((text) => text.trim() !== ''),
        apolloSequence,
      });
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-metadata-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="edit-metadata-title" className={styles.title}>
            Edit Metadata - {typeName}
          </h2>
          <IconButton
            size="small"
            aria-label="Close modal"
            onClick={onClose}
            className={styles.closeButton}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        {/* Content - scrollable section for objectives and painpoints */}
        <div className={styles.content}>
          {/* Website Objectives Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Website Objectives</h3>
            <div className={styles.items}>
              {objectives.map((objective) => (
                <Input
                  key={objective.id}
                  value={objective.text}
                  onChange={(value) => {
                    setObjectives((prev) =>
                      prev.map((obj) =>
                        obj.id === objective.id ? { ...obj, text: value } : obj
                      )
                    );
                  }}
                  clearable
                  className={styles.editableInput}
                />
              ))}
            </div>
          </div>

          {/* Industry Painpoints Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Industry Painpoints</h3>
            <div className={styles.items}>
              {painpoints.map((painpoint) => (
                <Input
                  key={painpoint.id}
                  value={painpoint.text}
                  onChange={(value) => {
                    setPainpoints((prev) =>
                      prev.map((pp) =>
                        pp.id === painpoint.id ? { ...pp, text: value } : pp
                      )
                    );
                  }}
                  clearable
                  className={styles.editableInput}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sequence section — outside scrollable content to avoid dropdown overflow clipping */}
        <div className={styles.sequenceSection}>
          <h3 className={styles.sectionTitle}>
            Default Apollo Sequence <span className={styles.optional}>(Optional)</span>
          </h3>
          <div className={styles.sequenceDropdownWrapper} ref={sequenceDropdownRef}>
            <button
              type="button"
              className={`${styles.sequenceTrigger} ${isSequenceDropdownOpen ? styles.sequenceTriggerOpen : ''}`}
              onClick={() => setIsSequenceDropdownOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={isSequenceDropdownOpen}
            >
              <span className={selectedSequenceName ? styles.sequenceValue : styles.sequencePlaceholder}>
                {selectedSequenceName ?? 'Select a sequence (optional)'}
              </span>
              <IconArrowDown size={16} className={styles.sequenceArrow} />
            </button>
            {isSequenceDropdownOpen && (
              <div className={styles.sequenceDropdown}>
                <MenuSelect
                  items={sequenceItems}
                  value={apolloSequence}
                  onChange={handleSequenceSelect}
                  maxHeight={200}
                  filterable={availableSequences.length > 5}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};
